import { IdentifierDTO } from '@zazume/zzm-base';
import { DateMonthYear, PricePrimitive, ZazumeService } from '@zazume/zzm-utils';

import { ErrorCode } from '#/lib/network/helpers';

import { BasicPayment, OnboardingBasicTenant, RentalConditions } from '.';
import { BasicContract } from './BasicContract';
import { BasicExpense } from './BasicExpense';
import { BasicUnitForApplication } from './BasicUnit';
import { BasicUser } from './BasicUser';
import {
  ComercializationPaymentFee,
  OnBoardingSettings,
  PropertyManagementPaymentFee,
  ZazumeCommercializationService
} from './Organization';
import { PortalIntegrationTypes } from './portalIntegration';
import { PreScoring } from './PreScoring';

export interface Application {
  _id: IdentifierDTO;
  archived?: ApplicationArchived;
  backgroundCheck?: BackgroundCheck;
  comercializationFee: ComercializationPaymentFee;
  comments?: ApplicationComment[];
  commercialisationPayment: CommercialisationPayment;
  contactDetails: ContactDetails;
  contractSettings?: ContractSettings;
  contractSigning?: ContractSigning;
  createdAt: Date;
  currentState: ApplicationStateType;
  //TODO: Change deposit to holdingDeposit
  declined?: ApplicationDeclined;
  deposit?: HoldingDeposit;
  documentsPresent?: boolean;
  endingDate?: Date;
  isApplicantQualified?: boolean;
  keyDeliveryId?: IdentifierDTO;
  manager?: BasicUser;
  moveIn?: MoveInState;
  onBoardingSettings: OnBoardingSettings;
  organizationId: IdentifierDTO;
  origin?: Origin;
  participants?: Participants;
  payments?: Payments;
  preScoring?: PreScoring;
  preScoringRejectReason?: PreScoringRejectReason;
  propertyManagementFee?: PropertyManagementPaymentFee;
  rentalConditions: RentalConditions;
  startingDate: Date;
  tenant: OnboardingBasicTenant;
  unit: BasicUnitForApplication;
  viewings?: Viewing;
  workspaceId: IdentifierDTO;
  zazumeCommercializationService?: ZazumeCommercializationService;
  zazumeService?: ZazumeService;
  noSuitableSchedule?: boolean;
  relatedAlertId?: boolean;
}

export const PreScoringRejectReasons = <const>['tenantProfile', 'pets', 'contractType', 'income', 'tenantsTotal'];
export type PreScoringRejectReason = (typeof PreScoringRejectReasons)[number];

export const DescriptionPMIds = [
  'other',
  'unitRented',
  'noApplicantResponse',
  'notInterested',
  'notFulfilRequirements',
  'rejectedAlertFeePayment',
  'wrongContactDetails'
] as const;
export const DescriptionTenantIds = ['other', 'alreadyLiving', 'dislikeUnit'] as const;
export const DescriptionOwnerIds = [
  'unitRented',
  'noApplicantResponse',
  'notInterested',
  'notEconomic',
  'notAcceptPets',
  'tooShort',
  'scoringWeak',
  'other'
] as const;
export type DescriptionPMIdsType = (typeof DescriptionPMIds)[number];
export type DescriptionTenantIdsType = (typeof DescriptionTenantIds)[number];
export type DescriptionOwnerIdsType = (typeof DescriptionOwnerIds)[number];
export type DescriptionIdsType = DescriptionPMIdsType | DescriptionTenantIdsType | DescriptionOwnerIdsType;

export interface ApplicationDeclined {
  user: BasicUser;
  descriptionId: DescriptionIdsType;
  descriptionExtraInfo: string;
  note?: string;
  at: Date;
}

export interface ApplicationArchived {
  status: ApplicationArchivedStatus;
  user: BasicUser;
  at: Date;
}

export interface Origin {
  portal: PortalIntegrationTypes;
  message?: string;
}

export type ApplicationArchivedStatus = 'living' | 'declined';

//TODO: Change deposit to holdingDeposit
export const ApplicationOptionalStates = <const>['deposit', 'payments', 'commercialisationPayment'];
export type ApplicationOptionalStateType = (typeof ApplicationOptionalStates)[number];

export const ApplicationStates = <const>['viewings', 'backgroundCheck', 'contractSigning', 'moveIn', 'living'];
export type ApplicationStateType = (typeof ApplicationStates)[number];

export type ApplicationStateAndOptionalStateType = ApplicationStateType | ApplicationOptionalStateType;

export const ApplicationStatesWithLeads = <const>['leads', ...ApplicationStates];
export type ApplicationStateWithLeadsType = (typeof ApplicationStatesWithLeads)[number];

export const UtilityType = <const>['electricity', 'water', 'gas', 'internet', 'insurance', 'alarm'];
export type UtilityTypeType = (typeof UtilityType)[number];

export const ActiveUtilityStates: UtilityStatesType[] = [
  'inProgress',
  'processed',
  'completed',
  'incident',
  'rejected'
];
export type ActiveUtilityStatesType = (typeof ActiveUtilityStates)[number];

export const DocumentIDTypes = <const>['CIF', 'DNI', 'NIE', 'other'];
export type DocumentIDType = (typeof DocumentIDTypes)[number];

export interface ContractSettings {
  activeExtension: boolean;
  activeNotifications: boolean;
  extensionMonths?: number;
  maxExtensionMonths?: number;
  notificationEndContractMonths?: number;
  notificationEndExtensionMonths?: number;
}

export interface Viewing {
  state: ApplicationStepStatusType;
  viewingId?: IdentifierDTO;
  commercialId?: IdentifierDTO;
  appointmentAt?: Date;
  timezone?: string;
  skippedAt?: Date;
  confirmed?: boolean;
}

export interface HoldingDeposit {
  state: ApplicationStepStatusType;
  payment?: BasicPayment;
  asAdditionalGuarantee?: boolean;
  documentId?: IdentifierDTO;
  skippedAt?: Date;
}

export interface BackgroundCheck {
  state: ApplicationStepStatusType;
  backgroundCheckId?: IdentifierDTO;
  skippedAt?: Date;
  documentationCompletedAt?: Date;
}

export interface ContractSigning {
  state: ApplicationStepStatusType;
  contract?: BasicContract;
  skippedAt?: Date;
}

export interface Payments {
  state: ApplicationStepStatusType;
  payments?: BasicPayment[];
  expenses?: BasicExpense[];
  skippedAt?: Date;
}

export interface CommercialisationPayment {
  state: ApplicationStepStatusType;
  documentId?: IdentifierDTO;
  payment?: BasicPayment;
  expenses?: BasicExpense[];
  skippedAt?: Date;
}

export interface MoveInState {
  externalProvider?: UtilitiesProvider;
  insuranceExternalProvider?: UtilitiesProvider;
  skippedAt?: Date;
  state: ApplicationStepStatusType;
  utilities: Utility[];
  requestBy?: IdentifierDTO;
  noManagement?: boolean;
  preferredTimeToCall?: string;
}

export const ApplicationStepStatus = <const>['completed', 'notStarted', 'started', 'waiting', 'toComplete', 'rejected'];
export type ApplicationStepStatusType = (typeof ApplicationStepStatus)[number];

export const UtilityStates = <const>[
  'pending',
  'dismissed',
  'inProgress',
  'processed',
  'completed',
  'incident',
  'rejected'
];
export type UtilityStatesType = (typeof UtilityStates)[number];

export interface Utility {
  type: UtilityTypeType;
  state: UtilityStatesType;
}

export interface UtilitiesProvider {
  id?: string;
  error?: ErrorCode;
  rawErrorMessage?: string;
  /**
   * Tells if Utilities are currently being sent to Provider
   */
  isInProgress?: boolean;
  issues?: string[];
  registeredAt?: Date;
}

export interface Participants {
  tenant: TenantInformation;
  sharers?: SharerInformation[];
  guarantors?: GuarantorInformation[];
}

export type Participant = TenantInformation | SharerInformation | GuarantorInformation;

export interface TenantInformation {
  _id: IdentifierDTO;
  name: string;
  surnames: string;
  fullName: string;
  avatar?: string;
  documentIDType: DocumentIDType;
  idNumber: string;
  employmentSituation?: EmploymentSituation;
  currentAddress?: CurrentAddress;
  phoneNumber: string;
  email: string;
  profileCompletionRate: number;
  missingDocuments?: ParticipantMissingDocuments;
}

export interface SharerInformation extends TenantInformation {}

export interface GuarantorInformation extends TenantInformation {}

export interface EmploymentSituation {
  situation?: EmploymentRole;
  startedAt?: Date;
  monthlyIncome?: number;
}

export interface EmploymentSituationWithCompleted extends Omit<EmploymentSituation, 'monthlyIncome'> {
  // TODO parse to PriceVO
  monthlyIncome?: PricePrimitive;
  isCompleted: boolean;
}

export interface CurrentAddress {
  fullAddress: string;
  town: string;
  province: string;
  zipCode: string;
  streetNumber: string;
  street: string;
  autonomousCommunity: string;
  country: string;
}

export const EmploymentRoles = <const>[
  'employee',
  'freelance',
  'business',
  'student',
  'official',
  'retired',
  'pensions',
  'withoutIncomes',
  'widowhood',
  'erte'
];
export type EmploymentRole = (typeof EmploymentRoles)[number];

export const ParticipantTypes = <const>['tenant', 'guarantor', 'sharer'];
export type ParticipantType = (typeof ParticipantTypes)[number];

export interface MissingDocument {
  type: DocumentType;
  requiredNumber: number;
  missingNumber: number;
}

export interface ParticipantMissingDocuments {
  participantId: IdentifierDTO;
  documents: MissingDocument[];
}

export interface BasicParticipant {
  _id: IdentifierDTO;
  name: string;
  avatar?: string;
  participantType: ParticipantType;
}

export interface TenantDetailsRequest {
  firstName: string;
  surnames: string;
  documentIDType: DocumentIDType;
  idNumber: string;
  contactNumber: string;
  email: string;
  iban: string;
  lang: string;
  currentAddress?: string;
  birthDate?: string;
  nationality?: string;
}

export interface UnitDetailsRequest {
  buildingId?: IdentifierDTO;
  workspaceId: IdentifierDTO;
  name: string;
  block?: string;
  floor?: string;
  door?: string;
  fullAddress?: string;
  rentalConditions: RentalConditions;
}

export interface ApplicationComment {
  _id: IdentifierDTO;
  user: BasicUser;
  text: string;
  createdAt: Date;
}

const initializationTenant: TenantDetailsRequest = {
  firstName: '',
  surnames: '',
  documentIDType: 'DNI',
  idNumber: '',
  contactNumber: '',
  email: '',
  iban: '',
  lang: '',
  birthDate: ''
};

export interface ScheduleViewingDetailsRequest {
  commercialId: IdentifierDTO;
  viewingDate: Date;
}

export const ContactStatusTypes = ['pending', 'contact_again', 'contacted'] as const;
export const ContactPreferenceTypes = ['whatsapp', 'portal_chat', 'phone', 'email'] as const;
export type ContactStatusType = (typeof ContactStatusTypes)[number];
export type ContactPreferenceType = (typeof ContactPreferenceTypes)[number];

export interface ContactDetails {
  status: ContactStatusType;
  preference?: ContactPreferenceType;
}

export class ApplicationRequestBody {
  tenantDetails: TenantDetailsRequest | IdentifierDTO = initializationTenant;
  unitDetails: IdentifierDTO = '';
  organizationId: IdentifierDTO = '';
  startingDate: string = '';
  contactDetails?: ContactDetails;

  static build(
    data,
    organizationId: IdentifierDTO,
    unitId: IdentifierDTO,
    tenantId?: IdentifierDTO
  ): ApplicationRequestBody {
    const unit: IdentifierDTO = unitId;

    const tenant: TenantDetailsRequest | IdentifierDTO = tenantId ?? {
      firstName: data.name,
      surnames: data.surnames,
      documentIDType: data.idCardType,
      idNumber: data.idNumber,
      contactNumber: data.phone,
      email: data.email,
      iban: data.iban,
      lang: data.language,
      currentAddress: data.currentAddress,
      birthDate: data.birthDate && DateMonthYear.fromPrimitive(new Date(data.birthDate)).toISO8601(),
      nationality: data.nationality
    };

    return {
      tenantDetails: tenant,
      unitDetails: unit,
      organizationId: organizationId,
      startingDate: data.startingDate,
      contactDetails: data.contactDetails
    };
  }
}

export const toBasicParticipant = (
  participant: SharerInformation | GuarantorInformation,
  participantType: ParticipantType
): BasicParticipant => {
  return {
    _id: participant.documentIDType + participant.idNumber,
    name: participant.name,
    participantType: participantType
  };
};
